import { FormattedMessage } from 'react-intl';
import { WP_Event } from '../../model/models';
import { Faq } from '../faq/Faq';
import Form from '../forms/Forms';
import GForm from '../gforms/GForm';
import HTMLContent from '../globals/HTMLContent';
import { SanitizeHTML } from '../globals/SanitizeHTML';
import { AddToCalIcon } from './AddToCalIcon';
import { ClockIcon } from './ClockIcon';
import { DateToIconHoriz } from './DateToIcon';
import { EventOrganisator } from './EventOrganisator';
import { HouseBadge } from './HouseBadge';
import { PlaceIcon } from './PlaceIcon';

interface EventModalProps {
  event: WP_Event;
  start_date: string;
  start_hour: string;
  end_date: string;
  end_hour: string;
  eventHouseThumbnailUrl: string;
  eventHouseName: string;
  places: [];
}

export const EventModalContent: React.FC<EventModalProps> = ({
  event,
  start_date,
  start_hour,
  end_date,
  end_hour,
  eventHouseThumbnailUrl,
  eventHouseName,
  places,
}) => {
  const img = event.fimg_url !== false ? (event.fimg_url as string) : '';
  const origin = event.acf.origin;
  const showHours = origin === 'portail';
  const event_places = (
    <>
      <div className="w-3/4">
        <h3 className="text-xs text-left font-sans font-medium mb-1 text-black">
          {places?.length > 1 ? (
            <FormattedMessage id="Event Places" />
          ) : (
            <FormattedMessage id="Event Place" />
          )}
        </h3>
        <ul className="inline-flex wrap text-sm text-left font-sans font-bold text-black">
          {places?.length > 0 &&
            places.map((place, id) => {
              const place_name: string = place['name'];
              const place_url: string = place['url'];
              return (
                <li key={id}>
                  {places?.length > 1 ? (
                    <a href={place_url} target="_blank" rel="noreferrer">
                      {place_name}
                    </a>
                  ) : (
                    <>{place_name}</>
                  )}
                  <span className="mr-1 text-black">
                    {places.length - 1 !== id ? ',' : ''}
                  </span>
                </li>
              );
            })}
        </ul>
      </div>
      <div className="flex justify-end w-1/4">
        <div className="bg-primary text-white p-2 rounded-full w-9 h-9 flex justify-center items-center">
          <PlaceIcon />
        </div>
      </div>
    </>
  );

  return (
    <div key={event.id} className="bg-white md:rounded-3xl md:p-5 h-auto">
      <HouseBadge
        addClass="modal-badge"
        thumbnailUrl={eventHouseThumbnailUrl}
        name={eventHouseName}
      />
      {!img && <div className="h-96 w-full bg-lightGrey md:rounded-3xl"></div>}
      {img && (
        <img
          className="h-96 w-full bg-lightGrey md:rounded-3xl object-cover"
          src={img}
          alt=""
        />
      )}
      <div className="rounded-t-3xl -mt-7 bg-white modal-news-content py-10 relative z-10 px-5 md:-mt-0">
        <div className="news-header">
          <SanitizeHTML
            tag="h1"
            html={event.title.rendered}
            className="text-left text-2xl font-bold mb-4 text-black"
          />
          <div className="news-header-author flex flex-row items-center">
            <EventOrganisator
              imgClass="w-8 h-8"
              textClass="text-xs"
              thumbnailUrl={eventHouseThumbnailUrl}
              name={eventHouseName}
            />
          </div>
        </div>
        <div className="flex items-center event-dates border-small border-primary rounded-2xl px-4 py-3 mt-4">
          <div className="flex items-center w-3/4">
            <div>
              <p className="text-sm font-sans font-medium text-black">
                {start_date}
              </p>
              {showHours && (
                <p className="inline-flex text-xs font-sans font-medium text-black">
                  <ClockIcon className="mr-1 h-3.5 w-3.5 text-primary" />
                  {start_hour}
                </p>
              )}
            </div>
            {end_date !== '' && (
              <>
                <DateToIconHoriz className="mx-8" />

                <div>
                  <p className="text-sm font-sans font-medium text-black">
                    {end_date}
                  </p>
                  {showHours && (
                    <p className="inline-flex text-xs font-sans font-medium text-black">
                      <ClockIcon className="mr-1 h-3.5 w-3.5 text-primary" />
                      {end_hour}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex justify-end items-center w-1/4">
            <div className="bg-primary text-white p-2 rounded-full">
              <AddToCalIcon />
            </div>
          </div>
        </div>

        {places?.length > 0 && places?.length < 2 ? (
          places.map((place, id) => {
            const place_url: string = place['url'];
            return place_url !== '' ? (
              <div className="flex items-center event-dates border-small border-primary rounded-2xl mt-4">
                <a
                  key={id}
                  className="flex items-center wrap w-full px-4 py-3"
                  href={place_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {event_places}
                </a>
              </div>
            ) : null;
          })
        ) : (
          <div className="w-full flex wrap px-4 py-3">{event_places}</div>
        )}

        {event.acf.practical !== undefined && (
          <div className="bg-lightGreen rounded-2xl mt-6 px-4 py-3">
            <h3 className="text-xs text-left font-sans font-medium mb-1 text-black">
              <FormattedMessage id="Useful infos" />
            </h3>
            <SanitizeHTML
              html={event.acf.practical}
              className="text-left text-sm event-content text-black font-sans font-semibold"
            />
          </div>
        )}
        <div className="styled-content mb-6">
          <h3 className="text-xs opacity-30 font-sans mb-2 text-black">
            Description
          </h3>
          <HTMLContent
            html={event.content.rendered}
            className="text-left text-sm text-black ion-text-wrap event-content"
          />
        </div>
        {event.acf.origin !== 'portail' && event.acf.origin !== undefined && (
          <a
            href={event.acf.external_url}
            target="_blank"
            rel="noreferrer"
            className="flex max-w-max mx-auto h-10 items-center px-6 py-4 uppercase border border-transparent shadow-sm text-sm font-bold rounded-3xl text-white bg-primary hover:bg-primary hover:bg-opacity-75 focus:outline-none "
          >
            <FormattedMessage id="Citescope" />
          </a>
        )}
      </div>
      {event.acf.faq && <Faq faq={event.acf.faq} />}
      {event.acf.form && <Form postId={event.id} form={event.acf.form} />}
      {event.acf.gform?.gform_activation === true && event.acf.gform.gform && (
        <GForm form={event.acf.gform.gform} />
      )}
    </div>
  );
};
